import React, { useContext, useState } from "react";
import { ScopesContext, type ScopeType } from "../ScopeProvider/ScopeProvider";
import "./LocationBreadcrumbs.scss";
import { pathLevels } from "../../AppViewUtils";
import {
  getIconClassName,
  resourceTypes,
} from "../../../Findings/FindingsUtils";
import { Tooltip, DotLoader } from "@SeemplicityDev/shared";

type PathLevel = (typeof pathLevels)[keyof typeof pathLevels];

// `resourceTypes` is still untyped JS (FindingsUtils.js), so values widen to
// `string`; once that file is migrated, narrow `PathItemType` to
// `ScopeType | PathLevel`.
// TODO(ts-migration): narrow when FindingsUtils is converted to TS.
type PathItemType = ScopeType | PathLevel | string;

const Arrow: React.FC = () => {
  return (
    <div className="bc-icon-wrap bc-icon-wrap-arrow">
      <i className="breadcrumbs-arrow seem-icon seem-icon-right-arrow" />
    </div>
  );
};

interface PathItemProps {
  type?: PathItemType;
  label?: string;
  onPathItemClick: (path: PathLevel) => void;
  isActive?: boolean;
  path?: PathLevel;
  isLoadingScopesData?: boolean;
  showLabel?: boolean;
}

const PathItem: React.FC<PathItemProps> = ({
  type,
  label,
  onPathItemClick,
  isActive,
  path,
  isLoadingScopesData,
}) => {
  if (type === pathLevels.MORE_ACTIONS) {
    return (
      <div
        onClick={() => path && onPathItemClick(path)}
        className={`bc-icon-wrap bc-icon-wrap-dots ${
          isActive ? "is-selected" : ""
        }`}
        title={label}
      >
        <i className="seem-icon seem-icon-more-actions" />
      </div>
    );
  }

  return (
    <Tooltip content={label || ""}>
      <div
        onClick={() => path && onPathItemClick(path)}
        className={`bc-item-wrap ${label ? "has-label" : ""} ${
          isActive ? "is-selected" : ""
        } ${isLoadingScopesData ? "is-loading" : ""}`}
        data-testid={`bc-item-wrap-${path}`}
      >
        {isLoadingScopesData ? (
          <div className="bc-item">
            <div className="bc-icon-wrap">
              <i className="seem-icon seem-icon-root"></i>
            </div>
            <DotLoader className={"location-breadcrumbs-loader"} />
          </div>
        ) : (
          <>
            <div className="bc-item">
              <div className="bc-icon-wrap">
                <i
                  className={`seem-icon ${getIconClassName(type ? type : "root")}`}
                />
              </div>
            </div>
            {label && <div className="bc-item-label">{label}</div>}
            <div className={`dropdown-indicator`}>
              <i className={`seem-icon seem-icon-chevron-down`} />
            </div>
          </>
        )}
      </div>
    </Tooltip>
  );
};

interface LocationBreadcrumbsProps {
  onPathItemClick: (path: PathLevel) => void;
  isScopesListOpen?: boolean;
  isLoadingScopesData?: boolean;
  closeScopesList: () => void;
}

const LocationBreadcrumbs: React.FC<LocationBreadcrumbsProps> = ({
  onPathItemClick,
  isScopesListOpen,
  isLoadingScopesData,
  closeScopesList,
}) => {
  const {
    scopesState: { selectedScope, isDirty, isNewScope },
  } = useContext(ScopesContext);
  const level = selectedScope?.level;

  const [activePath, setActivePath] = useState<PathLevel | null>(null);
  const label =
    isDirty || isNewScope ? "* " + selectedScope?.label : selectedScope?.label;

  function onPathClickHandler(path: PathLevel) {
    if (path === activePath && isScopesListOpen) {
      closeScopesList();
    } else {
      onPathItemClick(path);
      setActivePath(path);
    }
  }

  function getPath() {
    if (level === 0 || !level) {
      return (
        <div className={"breadcrumbs-wrapper"}>
          <PathItem
            isLoadingScopesData={isLoadingScopesData}
            path={pathLevels.ROOT}
            isActive={activePath === pathLevels.ROOT && isScopesListOpen}
            onPathItemClick={onPathClickHandler}
            label={selectedScope?.label}
            showLabel
          />
        </div>
      );
    }

    return (
      <>
        <PathItem
          path={level === 1 ? pathLevels.ROOT : pathLevels.DIRECT_PARENT}
          isActive={activePath === pathLevels.DIRECT_PARENT && isScopesListOpen}
          onPathItemClick={onPathClickHandler}
          type={
            level > 2
              ? pathLevels.MORE_ACTIONS
              : level === 1
                ? resourceTypes.ROOT
                : resourceTypes.GROUP
          }
        />
        <Arrow />
        <PathItem
          path={pathLevels.CURRENT}
          isActive={activePath === pathLevels.CURRENT && isScopesListOpen}
          onPathItemClick={onPathClickHandler}
          label={label}
          type={selectedScope?.value?.type}
        />
      </>
    );
  }

  return (
    <div
      className={`breadcrumbs-container levels-${level || pathLevels.ROOT}`}
      data-testid={"breadcrumbs-container"}
    >
      {getPath()}
    </div>
  );
};

export default LocationBreadcrumbs;
